import { Component, DesignerStyles, Layout, Panel, ResourceFileProps, TabProps } from "@mcleod/components";
import { Block, ScrollBehavior } from "@mcleod/core";
import { ResourceDesignerTab } from "@mcleod/designer";
import { PermissionsDesigner } from "./PermissionsDesigner";
import { PermissionsTabTooltip } from "./PermissionsTabTooltip";

export class PermissionsDesignerTab extends ResourceDesignerTab {
    designerPanel: Layout;
    designer: PermissionsDesigner;
    private _permedComponentMap: Map<string, Set<Component>> = new Map();
    private componentMap: Map<string, Map<string, Component>> = new Map();

    constructor(designer: PermissionsDesigner, props: ResourceFileProps, tabProps?: Partial<TabProps>) {
        super("layout", props, tabProps);
        this.designer = designer;
        this.designerPanel = new Layout({
            fillHeight: true,
            fillRow: true,
            _designer: designer,
            allowDecoratorOverride: true,
            applyFieldLevelPermissions: false,
            applyFieldLevelLicensing: false
        });
        this.add(this.designerPanel);
    }

    _populateComponentMap() {
        this.addToComponentMap(this.path, this.designerPanel);
        this.recurseIncludedComponents(this.path, this.designerPanel.discoverIncludedComponents(), this.addToComponentMap.bind(this));
    }

    recurseIncludedComponents(path: string, children: Component[], callback: (path: string, component: Component) => void) {
        if (children == null || path == null)
            return;
        children.forEach(child => {
            callback(path, child);
            if (child instanceof Layout && child.isNested === true) {
                this.recurseIncludedComponents(child.layoutName, child.discoverIncludedComponents(), callback);
            } else if (child instanceof Component) {
                if (child.deserialized === true)
                    this.recurseIncludedComponents(path, child.discoverIncludedComponents(), callback);
            }
        });
    }

    addToComponentMap(path: string, component: Component) {
        if (path == null || component?.selectableInDesigner !== true)
            return;
        let components = this.componentMap.get(path);
        if (!components) {
            components = new Map();
            this.componentMap.set(path, components);
        }
        components.set(component.id, component);
        this.syncPermedComponentMap(component);
    }

    get permedComponentMap(): Map<string, Set<Component>> {
        return this._permedComponentMap;
    }

    set permedComponentMap(value: Map<string, Set<Component>>) {
        this._permedComponentMap = value;
    }

    syncPermedComponentMap(component: Component, permed?: boolean) {
        const path = this.getComponentPath(component);
        if (permed == null) {
            permed = this.designer.permissionsSource
                .getSecureItemRowsForPage(path)
                .some(row => row.get("item_name") === component.id);
        }

        let components = this.permedComponentMap.get(path);
        if (permed) {
            if (!components) {
                components = new Set();
                this.permedComponentMap.set(path, components);
            }
            components.add(component);
        } else if (components) {
            components.delete(component);
            if (components.size === 0) {
                this.permedComponentMap.delete(path);
            }
        }
        this.syncPermedStyles(component);

        this.headingLabel.color = this.permedComponentMap.size > 0 ? "error" : "primary";
    }

    syncPermedStyles(component: Component, permed = this.isPermedComponent(component)) {
        component.setClassIncluded(DesignerStyles.permed, permed);
        if (this.designer.firstSelectedLayoutComponent === component) {
            component.setClassIncluded(DesignerStyles.permedSelected, permed);
            component.setClassIncluded(DesignerStyles.designerSelected, !permed);
        }
    }

    isPermedComponent(component: Component): boolean {
        return this.permedComponentMap.get(this.getComponentPath(component))?.has(component) ?? false;
    }

    private getComponentPath(component: Component): string {
        return component.owner?.layoutName ?? this.path;
    }

    permsChanged(component: Component, deleted: boolean) {
        const permed = !deleted;
        this.syncPermedComponentMap(component, permed);

        // sync up tabs that have this same component in a nested layout
        this.designer.tabset.tabs.forEach((tab: PermissionsDesignerTab) => {
            if (tab !== this) {
                const components: Map<string, Component> = tab.componentMap.get(this.path);
                if (components) {
                    const nestedComp = components.get(component.id);
                    if (nestedComp) {
                        tab.syncPermedComponentMap(nestedComp, permed);
                    }
                }
            }
        })
    }

    getSearchRequestEndpointAndBody(): [string, any] {
        const filter: any = {
            ...this.getSearchRequestBody(),
            apply_field_level_perms: false,
            apply_field_level_licensing: false,
            apply_field_level_company_type: false,
            apply_field_level_ltl_type: false,
            apply_field_level_experiment: false,
            apply_field_level_multi_currency: false
        };
        return [this.designer.endpointPath, filter];
    }

    selectAndScrollToComponent(layoutPath: string, component: Component) {
        if (component != null) {
            component.scrollIntoView({ behavior: ScrollBehavior.INSTANT, block: Block.CENTER });
            this.designer.selectComponent(this.path === layoutPath ? component : null);
        }
    }

    override async createTooltipPanel(): Promise<Panel> {
        if (this.permedComponentMap.size === 0)
            return null;
        return new PermissionsTabTooltip(this);
    }
}
